import dayjs from 'dayjs';
import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';
import { SyncAppointmentRecord } from 'src/hooks/appointment';
import {
  CSVParsedRow,
  CSVDateTimeFormats,
  CSV_SCHEMA_COLUMNS,
  BulkUploadSettings,
  REQUIRED_COLUMNS,
  DATE_TIME_COLUMNS,
  TIME_COLUMNS,
  DATE_COLUMNS,
} from './types';

type SchemaColumn = (typeof CSV_SCHEMA_COLUMNS)[number];

const convertDateFormat = (format: string | undefined): string => {
  if (!format) return 'MM/DD/YYYY';
  return format
    .replace(/%m/g, 'MM')
    .replace(/%d/g, 'DD')
    .replace(/%Y/g, 'YYYY')
    .replace(/%y/g, 'YY')
    .replace(/%H/g, 'HH')
    .replace(/%I/g, 'hh')
    .replace(/%M/g, 'mm')
    .replace(/%S/g, 'ss')
    .replace(/%p/g, 'A')
    .replace(/%-H/g, 'H')
    .replace(/%-I/g, 'h')
    .replace(/%-m/g, 'M')
    .replace(/%-d/g, 'D');
};

const createExcelDateConverters = (
  is1904: boolean,
  dateTimeFormats: CSVDateTimeFormats
) => {
  const epochYear = is1904 ? 1904 : 1900;
  const epochAdjustment = is1904 ? 0 : 2;
  const excelEpoch = new Date(epochYear, 0, 1);

  const excelDateTimeToFullDateTime = (serial: number | string): string => {
    if (typeof serial !== 'number') return String(serial);

    const datePart = Math.floor(serial);
    const timePart = serial - datePart;

    const days = datePart - epochAdjustment;
    const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);

    if (timePart === 0) {
      return dayjs(date).format(convertDateFormat(dateTimeFormats.date_format));
    }

    const totalMilliseconds = Math.round(timePart * 24 * 60 * 60 * 1000);
    const timeDate = new Date(totalMilliseconds);

    const hours = timeDate.getUTCHours();
    const minutes = timeDate.getUTCMinutes();
    const seconds = timeDate.getUTCSeconds();

    const timeMoment = dayjs().hour(hours).minute(minutes).second(seconds);

    return (
      dayjs(date).format(convertDateFormat(dateTimeFormats.date_format)) +
      ' ' +
      timeMoment.format(convertDateFormat(dateTimeFormats.time_format))
    );
  };

  const excelDateToFullDate = (serial: number | string): string => {
    if (typeof serial !== 'number') return String(serial);

    const days = serial - epochAdjustment;
    const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);

    return dayjs(date).format(convertDateFormat(dateTimeFormats.date_format));
  };

  const excelTimeToFullTime = (serial: number | string): string => {
    if (typeof serial !== 'number') return String(serial);

    const totalMilliseconds = Math.round(serial * 24 * 60 * 60 * 1000);
    const timeDate = new Date(totalMilliseconds);

    const hours = timeDate.getUTCHours();
    const minutes = timeDate.getUTCMinutes();
    const seconds = timeDate.getUTCSeconds();

    const timeMoment = dayjs().hour(hours).minute(minutes).second(seconds);

    return timeMoment.format(convertDateFormat(dateTimeFormats.time_format));
  };

  return {
    excelDateTimeToFullDateTime,
    excelDateToFullDate,
    excelTimeToFullTime,
  };
};

const getSchemaWithMappings = (
  columnMappings: BulkUploadSettings['column_mapping']
): SchemaColumn[] => {
  const schemaCopy = JSON.parse(
    JSON.stringify(CSV_SCHEMA_COLUMNS)
  ) as SchemaColumn[];

  if (columnMappings && Object.keys(columnMappings).length > 0) {
    for (const column of schemaCopy) {
      const mappedName = columnMappings[column.name];
      if (mappedName && mappedName !== '') {
        (column as { name: string }).name = mappedName;
      }
    }
  }

  return schemaCopy;
};

export const parseCSVFile = async (
  file: File,
  settings: BulkUploadSettings | undefined,
  dacEnabled: boolean
): Promise<CSVParsedRow[]> => {
  const schema = getSchemaWithMappings(settings?.column_mapping);
  const csvContent = await file.text();

  const parsedData: ParseResult<Record<string, unknown>> = Papa.parse(
    csvContent,
    {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header: string) => header.trim(),
      transform: (value: string) => {
        if (typeof value === 'string') {
          return value.replace(/\r\n/g, '\n');
        }
        return value;
      },
    }
  );

  return parsedData.data.map((row: Record<string, unknown>) => {
    const rowJson: CSVParsedRow = {};
    schema.forEach((column) => {
      if (dacEnabled) {
        if (column.key === 'start_date_time' || column.key === 'provider') {
          rowJson[column.key] = '';
        } else {
          rowJson[column.key] = row[column.name] as string | number | null;
        }
      } else {
        rowJson[column.key] = row[column.name] as string | number | null;
      }
    });
    return rowJson;
  });
};

export const parseExcelFile = async (
  file: File,
  settings: BulkUploadSettings | undefined,
  dacEnabled: boolean
): Promise<CSVParsedRow[]> => {
  const schema = getSchemaWithMappings(settings?.column_mapping);
  const dateTimeFormats: CSVDateTimeFormats = {
    date_format: settings?.date_format ?? '%m/%d/%Y',
    time_format: settings?.time_format ?? '%H:%M',
    birth_date_format: settings?.birth_date_format ?? '%m/%d/%Y',
  };

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const is1904 = workbook.Workbook?.WBProps?.date1904 ?? false;
  const {
    excelDateTimeToFullDateTime,
    excelDateToFullDate,
    excelTimeToFullTime,
  } = createExcelDateConverters(is1904, dateTimeFormats);

  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];

  const parsedData = XLSX.utils.sheet_to_json<(string | number | null)[]>(
    worksheet,
    {
      header: 1,
      raw: true,
    }
  );

  const headers = (parsedData[0] as string[]).map((header) =>
    String(header).trim()
  );
  if (dacEnabled) {
    headers.push('Start Date Time');
    headers.push('Provider');
  }

  const convertedData = parsedData.slice(1).filter((row) => row.length > 0);

  return convertedData.map((row) => {
    const rowJson: CSVParsedRow = {};
    schema.forEach((column) => {
      const headerIndex = headers.findIndex((header) => header === column.name);
      if (headerIndex !== -1) {
        let value = row[headerIndex] ?? null;

        if (value !== null && typeof value === 'number') {
          if (DATE_COLUMNS.includes(column.key) && value > 1000) {
            value = excelDateToFullDate(value);
          } else if (TIME_COLUMNS.includes(column.key) && value < 1) {
            value = excelTimeToFullTime(value);
          } else if (DATE_TIME_COLUMNS.includes(column.key) && value > 1000) {
            value = excelDateTimeToFullDateTime(value);
          }
        }

        rowJson[column.key] = value;
      }
    });
    return rowJson;
  });
};

export const parseFile = async (
  file: File,
  settings: BulkUploadSettings | undefined,
  dacEnabled: boolean
): Promise<CSVParsedRow[]> => {
  if (file.type === 'text/csv') {
    return parseCSVFile(file, settings, dacEnabled);
  }

  if (
    file.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel'
  ) {
    return parseExcelFile(file, settings, dacEnabled);
  }

  throw new Error(`Unsupported file type: ${file.type}`);
};

export const validateParsedData = (
  data: CSVParsedRow[]
): { valid: boolean; missingColumns: string[] } => {
  if (!data || data.length === 0) {
    return { valid: false, missingColumns: [] };
  }

  const firstRow = data[0];

  const patientColumns =
    firstRow.patient !== undefined
      ? (['patient'] as const)
      : (['patient_first_name', 'patient_last_name'] as const);

  const allRequiredColumns = [...REQUIRED_COLUMNS, ...patientColumns];

  const missingColumnKeys = allRequiredColumns.filter(
    (key) => firstRow[key] === undefined
  );

  if (missingColumnKeys.length === 0) {
    return { valid: true, missingColumns: [] };
  }

  const missingColumnNames = missingColumnKeys.reduce<string[]>((acc, key) => {
    const specialCases: Record<string, string> = {
      patient: 'Patient or Patient First Name & Patient Last Name',
      patient_first_name: 'Patient or Patient First Name & Patient Last Name',
      patient_last_name: 'Patient or Patient First Name & Patient Last Name',
    };

    const columnName =
      specialCases[key] ||
      CSV_SCHEMA_COLUMNS.find((column) => column.key === key)?.name;

    if (columnName && !acc.includes(columnName)) {
      return [...acc, columnName];
    }
    return acc;
  }, []);

  return { valid: false, missingColumns: missingColumnNames };
};

export const transformToSyncPayload = (
  data: CSVParsedRow[]
): SyncAppointmentRecord[] => {
  return data.map((record, index) => ({
    row_number: index + 2,
    appt_notes: String(record.appt_notes ?? '').trim(),
    misc_column: String(record.misc_column ?? '').trim(),
    appointment: {
      appointment_status: String(record.appointment_status ?? ''),
      created_time: String(record.created_date_time ?? ''),
      start_time: record.start_date_time
        ? String(record.start_date_time)
        : record.start_date
          ? `${record.start_date} ${record.start_time ?? ''}`
          : '',
      practitioner_name: String(record.provider ?? '').trim(),
      condition_name: String(record.appointment_type ?? ''),
    },
    patient: {
      birth_date: String(record.birth_date ?? ''),
      name: record.patient
        ? String(record.patient).trim()
        : `${String(record.patient_first_name ?? '').trim()} ${String(record.patient_last_name ?? '').trim()}`,
      ehr_id: String(record.patient_ehr_id ?? ''),
      gender: String(record.sex ?? ''),
      address: {
        line_1: String(record.primary_address_1 ?? ''),
        line_2: String(record.primary_address_2 ?? ''),
        city: String(record.city ?? ''),
        state: String(record.state ?? ''),
        zip: String(record.zip ?? ''),
      },
      phone: record.phone_number ? String(record.phone_number).trim() : '',
      email: String(record.email ?? '').trim(),
    },
  }));
};

export const generateS3Key = (fileName: string): string => {
  const date = new Date();
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const timestamp = date.getTime();
  const extension = fileName.split('.').pop();

  return `${year}/${month}/${day}/${timestamp}.${extension}`;
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
